import { useEffect, useState, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { Link } from 'react-router-dom';
import postApi from '../../api/postApi';
import shopeeApi from '../../api/shopeeApi';
import { LinkSource } from '../../constants/link-source';
import AnswerQuestion from '../../components/Q&A/Q&A';
import Header from '../../components/Header';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import FindOrderMini from '../../components/Ecommerce/FindOrderMini';
import VideoCard from './video-card';

const PAGE_LIMIT = 24;

function VideoList() {
    const { userInfo } = useSelector((state) => state.auth);
    const isAdmin = userInfo && userInfo.role === 'admin';

    const [isOpenModal, setIsOpenModal] = useState(false);
    const [removeCatId, setRemoveCatId] = useState();

    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(''); // '' = all
    const [keyword, setKeyword] = useState('');
    const [inputValue, setInputValue] = useState('');
    const debounceRef = useRef(null);

    const [videos, setVideos] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // ─── Fetch categories ─────────────────────────────────────────────
    const fetchCategories = async () => {
        try {
            const { data } = await postApi.getVideoCategories();
            setCategories(data.videoCategories || []);
        } catch (error) {
            console.error('[VideoList] fetchCategories:', error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    // ─── Fetch videos ──────────────────────────────────────────────────
    const fetchVideos = useCallback(async (categoryId, kw, pageNum, append = false) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                withProducts: '1',
                hideDead: '1',
                sort: '-id',
                page: pageNum,
                limit: PAGE_LIMIT,
            });
            if (categoryId) params.set('categoryId', categoryId);
            if (kw) params.set('productName', kw);

            const { data } = await postApi.getVideoList(params.toString());
            const newVideos = data.videos || [];
            setVideos((prev) => (append ? [...prev, ...newVideos] : newVideos));
            // backend may return total; fallback: if we got a full page, assume there's more
            setHasMore(newVideos.length === PAGE_LIMIT);
        } catch (error) {
            console.error('[VideoList] fetchVideos:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Reset to page 1 whenever filter changes
    useEffect(() => {
        setPage(1);
        fetchVideos(selectedCategoryId, keyword, 1, false);
    }, [selectedCategoryId, keyword, fetchVideos]);

    // ─── Search debounce ───────────────────────────────────────────────
    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setKeyword(val);
        }, 500);
    };

    // ─── Load more ─────────────────────────────────────────────────────
    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchVideos(selectedCategoryId, keyword, nextPage, true);
    };

    // ─── Mở link: có login → gắn affiliate (convert-link, ghi ClickLog) rồi mở;
    //     chưa login → mở thẳng url (giữ hành vi cũ).
    //     Mở tab trống TRƯỚC (đồng bộ) rồi đổi location sau await → tránh bị chặn popup.
    const openAffiliateOrDirect = useCallback(async (rawUrl) => {
        if (!rawUrl) return;
        if (!userInfo) {
            window.open(rawUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        const win = window.open('about:blank', '_blank');
        if (win) win.opener = null;
        try {
            const { data } = await shopeeApi.convertLink(rawUrl, LinkSource.ShopeeVideo);
            const short =
                data?.data?.batchCustomLink?.[0]?.shortLink || data?.data?.shortLink || rawUrl;
            if (win) win.location.href = short;
            else window.open(short, '_blank', 'noopener,noreferrer');
        } catch {
            if (win) win.location.href = rawUrl;
            else window.open(rawUrl, '_blank', 'noopener,noreferrer');
        }
    }, [userInfo]);

    const handleVideoClick = useCallback((video) => {
        openAffiliateOrDirect(video?.url);
    }, [openAffiliateOrDirect]);

    const handleProductClick = useCallback((product) => {
        openAffiliateOrDirect(product?.productLink);
    }, [openAffiliateOrDirect]);

    // ─── Admin: delete video ───────────────────────────────────────────
    const handleDeleteVideo = async (videoId) => {
        try {
            await postApi.deleteVideoList(videoId);
            setVideos((prev) => prev.filter((v) => v.id !== videoId));
        } catch (error) {
            console.error('[VideoList] deleteVideo:', error);
        }
    };

    // ─── Admin: delete category ────────────────────────────────────────
    const onSetRemoveCatId = (categoryId) => {
        setRemoveCatId(categoryId);
        setIsOpenModal(true);
    };

    const onDeleteVideoCate = async () => {
        try {
            await postApi.deleteVideoCategory(removeCatId);
            setCategories((prev) => prev.filter((c) => c.categoryId !== removeCatId));
            if (selectedCategoryId === removeCatId) setSelectedCategoryId('');
        } catch (error) {
            console.error('[VideoList] deleteCategory:', error);
        }
        setIsOpenModal(false);
    };

    return (
        <div className="min-h-dvh bg-surface-muted flex flex-col md:w-[768px] lg:w-[1024px] md:mx-auto">
            <Header hasBackButton={true} redirectPath="/" />

            <div className="p-4 flex flex-col gap-4 pt-20">
                {/* ── Page header ── */}
                <div>
                    <div className="flex justify-center items-center">
                        <div className="h-20">
                            <img
                                src="/img/ui/shopeevideo.png"
                                alt="Shopee Video"
                                className="h-full"
                            />
                        </div>
                        <span className="font-bold text-xl text-orange-600">
                            Shopee Video
                        </span>
                    </div>
                    <p className="text-center font-semibold">
                        Mã giảm giá đến 40% + Hoàn tiền đến 30%
                    </p>
                </div>

                {/* ── Search ── */}
                <div>
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="Tìm kiếm sản phẩm, thương hiệu..."
                        className="p-3 w-full rounded-md border border-orange-500 bg-surface"
                    />
                </div>

                {/* ── Instructions ── */}
                <div className="bg-surface border rounded-lg text-sm">
                    <div className="flex p-2 border-b last:border-none">
                        <p>
                            Gõ tìm kiếm tên sản phẩm hoặc tên thương hiệu bạn cần mua.
                            Ví dụ: Nước tẩy trang hoặc L&apos;Oreal
                        </p>
                    </div>
                    <div className="flex p-2 border-b last:border-none">
                        <p>
                            Voucher độc quyền chỉ áp dụng duy nhất tại kênh video{' '}
                            <Link
                                target="_blank"
                                className="underline text-orange-600 font-bold"
                                to="https://s.shopee.vn/8UuWIkdsyy"
                            >
                                Longhousee
                            </Link>
                        </p>
                    </div>
                    <div className="flex p-2 border-b last:border-none">
                        <p>
                            Ưu đãi độc quyền, mua hàng tại kênh video Longhousee được hoàn
                            tiền lên tới 30%. Chi tiết xem{' '}
                            <Link
                                to="/post/16"
                                className="underline text-orange-600 font-bold"
                                target="_blank"
                            >
                                tại đây
                            </Link>
                        </p>
                    </div>
                </div>

                {/* ── Category filter chips ── */}
                <div>
                    <h4 className="font-bold mb-2">Danh mục</h4>
                    <div className="flex gap-2 flex-wrap">
                        {/* "Tất cả" chip */}
                        <button
                            onClick={() => setSelectedCategoryId('')}
                            className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                                selectedCategoryId === ''
                                    ? 'bg-orange-500 text-white border-orange-500'
                                    : 'bg-surface text-content-2 border-line-strong hover:border-orange-400'
                            }`}
                        >
                            Tất cả
                        </button>
                        {categories.map((cat) => (
                            <div key={cat.categoryId} className="flex items-center gap-1">
                                <button
                                    onClick={() => setSelectedCategoryId(cat.categoryId)}
                                    className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                                        selectedCategoryId === cat.categoryId
                                            ? 'bg-orange-500 text-white border-orange-500'
                                            : 'bg-surface text-content-2 border-line-strong hover:border-orange-400'
                                    }`}
                                >
                                    🔥 {cat.categoryName}
                                </button>
                                {isAdmin && (
                                    <button
                                        onClick={() => onSetRemoveCatId(cat.categoryId)}
                                        className="text-content-3 hover:text-orange-600 p-1"
                                        title="Xóa danh mục"
                                        aria-label="Xóa danh mục"
                                    >
                                        <FontAwesomeIcon icon={faTrash} size="xs" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Video card grid ── */}
                <div>
                    {isLoading && videos.length === 0 ? (
                        /* Skeleton placeholders */
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="bg-surface rounded-xl overflow-hidden border border-line">
                                    <div className="w-full bg-surface-raised animate-pulse" style={{ paddingTop: '133%' }} />
                                    <div className="p-2 flex flex-col gap-1">
                                        <div className="h-3 bg-surface-raised animate-pulse rounded w-full" />
                                        <div className="h-3 bg-surface-raised animate-pulse rounded w-2/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : videos.length === 0 ? (
                        <p className="text-center text-content-3 py-10">
                            Không tìm thấy video nào.
                        </p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {videos.map((video) => (
                                <VideoCard
                                    key={video.id}
                                    video={video}
                                    isAdmin={isAdmin}
                                    onDelete={handleDeleteVideo}
                                    onProductClick={handleProductClick}
                                    onVideoClick={handleVideoClick}
                                />
                            ))}
                        </div>
                    )}

                    {/* Load more */}
                    {hasMore && !isLoading && (
                        <div className="flex justify-center mt-4">
                            <button
                                onClick={handleLoadMore}
                                className="px-6 py-2 bg-orange-500 text-white rounded-full text-sm font-semibold hover:bg-orange-600 transition-colors"
                            >
                                Xem thêm
                            </button>
                        </div>
                    )}
                    {isLoading && videos.length > 0 && (
                        <div className="flex justify-center mt-4">
                            <span className="text-content-3 text-sm">Đang tải...</span>
                        </div>
                    )}
                </div>

                {/* ── Find order ── */}
                <div>
                    <FindOrderMini merchant="shopee" title="Nhập mã đơn Video" />
                </div>

                {/* ── How to earn cashback ── */}
                <div>
                    <div className="flex gap-2 items-center py-2">
                        <div className="h-10 aspect-square bg-gradient-to-br p-2 from-orange-500 to-orange-600 rounded-full">
                            <img
                                src="/img/ui/information_white.svg"
                                alt="Hướng dẫn"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <h3 className="font-bold">HƯỚNG DẪN CÁCH ÁP HOÀN TIỀN VIDEO</h3>
                    </div>

                    <div className="flex flex-col gap-4">
                        {/* Method 1 */}
                        <div className="bg-surface border rounded-lg text-sm">
                            <h4 className="p-2 border-b font-semibold text-orange-600">
                                Cách 1: Mua hàng trực tiếp từ kênh Shopee Longhousee
                            </h4>
                            {[
                                <>
                                    Bạn tìm kiếm sản phẩm muốn mua hoặc truy cập trực tiếp mua hàng tại kênh:{' '}
                                    <Link target="_blank" to="https://s.shopee.vn/8UuWIkdsyy" className="underline text-orange-600">
                                        https://s.shopee.vn/8UuWIkdsyy
                                    </Link>{' '}
                                    (không cần dán link vào web)
                                </>,
                                <>
                                    Sau 48H kể từ khi thanh toán đơn hàng, bạn cần copy Mã đơn hàng và dán vào mục tìm
                                    kiếm "Nhập mã đơn Video" phía trên để thực hiện xác nhận đơn hàng.
                                </>,
                                <>
                                    Kiểm tra số tiền được hoàn{' '}
                                    <Link to="/user-info/order" className="underline text-primary-500 font-semibold">
                                        TẠI ĐÂY
                                    </Link>
                                </>,
                            ].map((step, i) => (
                                <div key={i} className="flex p-2 border-b last:border-none">
                                    <div className="line-clamp-1 w-14 shrink-0 flex justify-center items-center p-1 border-r">
                                        <span>B{i + 1}</span>
                                    </div>
                                    <p className="pl-2">{step}</p>
                                </div>
                            ))}
                            <p className="p-2">
                                Lưu ý: Việc xác nhận đơn do Shopee dựa vào quy định riêng để tính toán đơn hợp lệ.
                                Vì thế vẫn có tỉ lệ 1 số đơn làm đúng quy trình vẫn không được duyệt hoặc không tìm
                                thấy đơn.
                            </p>
                        </div>

                        {/* Method 2 */}
                        <div className="bg-surface border rounded-lg text-sm">
                            <h4 className="p-2 border-b font-semibold text-orange-600">
                                Cách 2: Mua hàng trực tiếp từ kênh video Shop bán hàng
                            </h4>
                            {[
                                <>
                                    Vào video của Shop thêm sản phẩm vào giỏ cần mua + copy link sản phẩm (không đặt
                                    hàng thông qua Video của KOC/KOL tích vàng, có thể xem video hướng dẫn{' '}
                                    <Link to="https://www.youtube.com/watch?v=UULz-Q-xDwY" target="_blank" className="underline text-primary-600">
                                        TẠI ĐÂY
                                    </Link>
                                    )
                                </>,
                                <>
                                    Truy cập vào{' '}
                                    <Link to="https://longhousee.com/shopee" className="underline text-primary-600">
                                        Mua sắm hoàn tiền Shopee
                                    </Link>{' '}
                                    và dán link sản phẩm để gắn hoàn tiền. Sau đó bấm "Mua ngay"
                                </>,
                                <>Thêm sản phẩm vào giỏ lần 2. Sau đó vào giỏ hàng điều chỉnh số lượng cần mua + Áp mã và đặt đơn</>,
                                <>
                                    Sau 48 giờ kể từ khi thanh toán đơn hàng, bạn copy mã đơn hàng và dán vào mục
                                    "Tìm đơn Shopee Video"{' '}
                                    <Link className="underline text-orange-600 font-semibold" to="/shopee">
                                        TẠI ĐÂY
                                    </Link>{' '}
                                    là đơn hàng sẽ hiện trên hệ thống nhé.
                                </>,
                                <>
                                    Kiểm tra số tiền được hoàn{' '}
                                    <Link to="/user-info/order" className="underline text-primary-500 font-semibold">
                                        TẠI ĐÂY
                                    </Link>
                                </>,
                            ].map((step, i) => (
                                <div key={i} className="flex p-2 border-b last:border-none">
                                    <div className="line-clamp-1 w-14 shrink-0 flex justify-center items-center p-1 border-r">
                                        <span>B{i + 1}</span>
                                    </div>
                                    <p className="pl-2">{step}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <AnswerQuestion postId={17} />
                <div className="pb-10" />
            </div>

            {/* Admin: confirm delete category */}
            <Modal
                title="Xác nhận xoá Danh mục Video?"
                open={isOpenModal}
                onOk={onDeleteVideoCate}
                onCancel={() => setIsOpenModal(false)}
            />
        </div>
    );
}

export default VideoList;
